import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';

export default function TransferModal({ board, type, onClose, onConfirm }) {
  const [username, setUsername] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const labels = {
    sold: { title: 'Sell Board', desc: 'Transfer full ownership to the buyer.', btn: 'Confirm Sale', btnClass: 'bg-blue-500 text-white' },
    lent: { title: 'Lend Board', desc: 'Board stays in your garage but is marked as lent.', btn: 'Confirm Lend', btnClass: 'bg-yellow-500 text-black' },
    gifted: { title: 'Gift Board', desc: 'Transfer full ownership as a gift.', btn: 'Confirm Gift', btnClass: 'bg-pink-500 text-white' },
  };
  const cfg = labels[type] || labels.sold;

  const handleSubmit = async () => {
    if (!username.trim()) { setError('Username is required'); return; }
    setLoading(true);
    setError('');
    const res = await onConfirm(board.board_id, type, username.trim(), notes.trim());
    setLoading(false);
    if (res?.error) setError(res.error);
    else onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm bg-bg-paper border border-white/10 rounded-2xl p-5 shadow-2xl"
        data-testid="transfer-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-sm font-semibold">{cfg.title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full"><X className="w-4 h-4 text-muted-foreground" /></button>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">{cfg.desc}</p>
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-3">
          <p className="text-[10px] text-muted-foreground/60 mb-0.5">Board</p>
          <p className="text-xs font-medium">{board.name}</p>
          <p className="text-[9px] font-mono text-muted-foreground/40">{board.serial}</p>
        </div>
        <div className="space-y-2 mb-4">
          <input
            data-testid="transfer-username-input"
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="recipient's username"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
          />
          <input
            data-testid="transfer-notes-input"
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="notes (optional)"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
          />
        </div>
        {error && <p data-testid="transfer-error" className="text-[11px] text-red-400 mb-3">{error}</p>}
        <div className="flex gap-2">
          <button
            data-testid="transfer-confirm-btn"
            onClick={handleSubmit}
            disabled={loading}
            className={`flex-1 text-xs font-bold py-2.5 rounded-xl transition-opacity ${cfg.btnClass} ${loading ? 'opacity-50' : 'hover:opacity-90'}`}
          >
            {loading ? 'Processing...' : cfg.btn}
          </button>
          <button onClick={onClose} className="px-4 bg-white/5 border border-white/10 text-muted-foreground text-xs py-2.5 rounded-xl hover:border-white/20">Cancel</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
